import { atom, computed, type WritableAtom } from 'nanostores';
import { ProjectEnvironment, Project, ProjectVersion } from '~/services/versori-api/types';
import { workbenchStore } from './workbench';

export type AnonProject = {
  id: string;
  name: string;
  connectorId: never;
  environments: never;
  currentFiles: never;
  createdAt: never;
  settings: never;
  metadata: never;
};

export class ProjectStore {
  project: WritableAtom<Project> = atom<Project>({} as Project);
  versions: WritableAtom<ProjectVersion[]> = atom<ProjectVersion[]>([]);
  currentVersion: WritableAtom<string> = atom<string>('');
  /* Track whether we're viewing the "current" (latest draft) version */
  isCurrentVersion: WritableAtom<boolean> = atom<boolean>(true);
  currentEnvironment: WritableAtom<ProjectEnvironment> = atom<ProjectEnvironment>({} as ProjectEnvironment);
  isInitialised: WritableAtom<boolean> = atom<boolean>(false);

  constructor() {
    this.isInitialised.set(false);

    this.project.listen((project) => {
      if (!project.environments?.length) return;
      const defaultEnvironmentId = project.settings?.defaultEnvironment;
      const defaultEnvironment =
        (defaultEnvironmentId && project.environments.find((environment) => environment.id === defaultEnvironmentId)) ||
        project.environments[0];

      if (!this.isInitialised.get()) {
        this.isInitialised.set(true);
        this.currentEnvironment.set(defaultEnvironment);
        return;
      }

      const currentEnv = this.currentEnvironment.get();
      const updatedEnv = project.environments.find((env) => env.id === currentEnv.id);
      if (updatedEnv) {
        this.currentEnvironment.set(updatedEnv);
        return;
      }

      this.currentEnvironment.set(defaultEnvironment);
    });
  }

  activeVersion = computed(
    [this.currentVersion, this.versions, this.isCurrentVersion],
    (currentVersion, versions, isCurrentVersion) => {
      if (isCurrentVersion) {
        return undefined;
      }
      const version = versions.find((version) => version.id === currentVersion);
      return version;
    }
  );

  projectName = computed([this.project, workbenchStore.files], (project, files) => {
    if (!project.name) {
      return undefined;
    }
    if (!project.name.startsWith('v-')) {
      return project.name;
    }
    const packageJson = files['package.json'];
    if (packageJson) {
      try {
        const content = JSON.parse(packageJson.content);
        return content.name || project.name;
      } catch {
        // Expected while the agent is still streaming package.json into the
        // workbench — the partial JSON is unparsable until the final token
        // lands. Fall through to the current name without logging.
      }
    }
    return project.name;
  });

  setProject(project: Project) {
    this.project.set(project);
  }

  setCurrentEnvironment(environment: ProjectEnvironment) {
    this.currentEnvironment.set(environment);
  }

  setVersions(versions: ProjectVersion[]) {
    this.versions.set(versions);
  }

  setCurrentVersion(currentVersion: string) {
    this.currentVersion.set(currentVersion);
  }

  setIsCurrentVersion(isCurrentVersion: boolean) {
    this.isCurrentVersion.set(isCurrentVersion);
  }

  updateProjectFile(filePath: string, content: string) {
    const project = this.project.get();
    if (!project) {
      return;
    }
    const exists = project.currentFiles.files.some((file) => file.filename === filePath);
    project.currentFiles.files = exists
      ? project.currentFiles.files.map((file) => (file.filename === filePath ? { ...file, content } : file))
      : [...project.currentFiles.files, { filename: filePath, content }];
    this.project.set(project);
  }

  reset() {
    this.project.set({} as Project);
    this.versions.set([]);
    this.currentVersion.set('');
    this.isCurrentVersion.set(true);
    this.currentEnvironment.set({} as ProjectEnvironment);
    this.isInitialised.set(false);
  }
}

export const projectStore = new ProjectStore();
